import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Lock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { tiers } from '../data/tiers';
import type { Expert } from '../types/expert';
import { getTier, nextTier, tierBreakdown, tierScore, tierStyles } from '../utils/tier';
type ExpertTierPanelProps = {
  expert: Expert;
};
export function ExpertTierPanel({
  expert
}: ExpertTierPanelProps) {
  const score = tierScore(expert);
  const tier = getTier(expert);
  const next = nextTier(tier);
  const breakdown = tierBreakdown(expert);
  const style = tierStyles[tier.id];
  const isDark = tier.id === 'ambassador';
  const TierIcon = tier.icon;
  return <div className="flex flex-col gap-4" data-id="element-296" data-mp="15n6e9lcvob8q">
      <div className={`overflow-hidden rounded-xl border ${isDark ? 'border-neutral-800 bg-neutral-900 text-white' : `bg-card`}`} data-id="element-297" data-mp="hywv6r71u1la">
        <div className="grid gap-6 p-6 md:grid-cols-[1fr_320px] md:items-center" data-id="element-298" data-mp="dwidiaf497vl">
          <div className="flex items-center gap-4" data-id="element-299" data-mp="3198y0ebl6q5j">
            <span className={`flex size-14 items-center justify-center rounded-xl ${style.solid}`} data-id="element-300" data-mp="316mqgeop4anb">
              <TierIcon className="size-7" aria-hidden="true" data-id="element-301" data-mp="365pkl4350wgi" />
            </span>
            <div data-id="element-302" data-mp="3az0tdarifnb2">
              <p className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-amber-300' : 'text-muted-foreground'}`} data-id="element-303" data-mp="3b0lmqx23907c">
                Tier {tier.level} of 4
              </p>
              <h3 className="text-xl font-semibold" data-id="element-304" data-mp="2ibo60ydizbgp">{tier.name} <span className={`text-sm font-normal ${isDark ? 'text-neutral-400' : 'text-muted-foreground'}`} lang="ar" data-id="element-305" data-mp="2n8emhmt8s92n">· {tier.nameAr}</span></h3>
              <p className={`text-sm ${isDark ? 'text-neutral-300' : 'text-muted-foreground'}`} data-id="element-306" data-mp="2s6pwpyng8lle">{tier.tagline}</p>
            </div>
          </div>
          <div data-id="element-307" data-mp="7ibkhi4gg1i8">
            <div className="flex items-end justify-between" data-id="element-308" data-mp="7gqqbtm1ykkw">
              <span className="text-3xl font-semibold tabular-nums" data-id="element-309" data-mp="cgv95qafnrea">{score}</span>
              <span className={`text-xs ${isDark ? 'text-neutral-400' : 'text-muted-foreground'}`} data-id="element-310" data-mp="cizm0xqb80qp">
                {next ? `${(next.minScore - score).toFixed(1)} pts to ${next.name}` : 'Highest tier reached'}
              </span>
            </div>
            <div className={`mt-2 flex h-2 gap-1`} aria-hidden="true" data-id="element-311" data-mp="2hkzl7tw43z65">
              {tiers.map(t => {
              const lo = t.minScore;
              const hi = nextTier(t)?.minScore ?? 100;
              const fill = Math.max(0, Math.min(1, (score - lo) / (hi - lo))) * 100;
              return <div key={t.id} className={`relative flex-1 overflow-hidden rounded-full ${isDark ? 'bg-neutral-700' : 'bg-muted'}`} data-id="element-312" data-mp="1kvqgq9jw3tzt">
                    <div className={`h-full ${isDark ? 'bg-amber-300' : style.bar}`} style={{
                  width: `${fill}%`
                }} data-id="element-313" data-mp="1ynolk86kj3q6" />
                  </div>;
            })}
            </div>
            <div className={`mt-1.5 grid grid-cols-4 text-[10px] ${isDark ? 'text-neutral-400' : 'text-muted-foreground'}`} data-id="element-314" data-mp="163g9te8dopwi">
              {tiers.map(t => <span key={t.id} data-id="element-315" data-mp="rzhdc1a41jvo">{t.name}</span>)}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[340px_1fr]" data-id="element-316" data-mp="wqza0d53oyau">
        <Card data-id="element-317" data-mp="wsk8ueyg73ei">
          <CardHeader data-id="element-318" data-mp="i2352oycc1eg">
            <CardTitle data-id="element-319" data-mp="i2mfzlpkppiz">How the score is built</CardTitle>
            <CardDescription data-id="element-320" data-mp="i006vhn1b1yn">Recalculated after every engagement and review.</CardDescription>
          </CardHeader>
          <CardContent data-id="element-321" data-mp="1aqrys2x2h4g6">
            <ul className="space-y-4" data-id="element-322" data-mp="1aq8o979qxic6">
              {breakdown.map(f => <li key={f.key} data-id="element-323" data-mp="1fq4wjiph7vxj">
                  <div className="flex justify-between text-sm" data-id="element-324" data-mp="1foac05i4a5tz">
                    <span className="font-medium" data-id="element-325" data-mp="10suajff7j7nk">{f.label}</span>
                    <span className="tabular-nums text-muted-foreground" data-id="element-326" data-mp="10uf9ricgverr">{f.points.toFixed(1)} / {f.max}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-muted" data-id="element-327" data-mp="3li1up08wcnag">
                    <div className="h-1.5 rounded-full bg-primary" style={{
                  width: `${f.points / f.max * 100}%`
                }} data-id="element-328" data-mp="3lkwfisvtoa4e" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground" data-id="element-329" data-mp="akgeym75ds3x">{f.detail}</p>
                </li>)}
            </ul>
            <Link to="/tiers" className="mt-5 inline-block text-xs font-medium underline-offset-4 hover:underline" data-id="element-330" data-mp="5rdju9mq2alm">
              View all tiers & criteria →
            </Link>
          </CardContent>
        </Card>

        <Card data-id="element-331" data-mp="3ry1zh91r2ot2">
          <CardHeader data-id="element-332" data-mp="ti99b03pgtpp">
            <CardTitle data-id="element-333" data-mp="tiixci56wqsr">Privileges</CardTitle>
            <CardDescription data-id="element-334" data-mp="thhj64svno6t">Unlocked benefits, and what becomes available at higher tiers.</CardDescription>
          </CardHeader>
          <CardContent data-id="element-335" data-mp="jjteam1k50ax">
            <div className="space-y-5" data-id="element-336" data-mp="jja6vz9j9ub5">
              {tiers.map(t => {
              const unlocked = t.level <= tier.level;
              return <section key={t.id} aria-label={`${t.name} privileges`} data-id="element-337" data-mp="encto1tkh42l">
                    <p className={`mb-2 flex items-center gap-1.5 text-xs font-semibold ${unlocked ? 'text-foreground' : 'text-muted-foreground'}`} data-id="element-338" data-mp="34bltod11hxml">
                      {unlocked ? <CheckCircle2 className="size-3.5 text-emerald-600" aria-hidden="true" data-id="element-339" data-mp="1etyeb92s9o5n" /> : <Lock className="size-3.5" aria-hidden="true" data-id="element-340" data-mp="1js04edymmnqm" />}
                      {t.name} {unlocked ? '· Unlocked' : `· Unlocks at ${t.minScore} pts`}
                    </p>
                    <ul className="grid gap-2 sm:grid-cols-2" data-id="element-341" data-mp="1ti2vu9q3ntmu">
                      {t.privileges.map(p => {
                    const PIcon = p.icon;
                    return <li key={p.title} className={`flex gap-3 rounded-lg border p-3 ${unlocked ? 'bg-card' : 'border-dashed bg-muted/30 opacity-60'}`} data-id="element-342" data-mp="1yjil6dzb8mb1">
                            <span className={`flex size-8 shrink-0 items-center justify-center rounded-md ${unlocked ? tierStyles[t.id].solid : 'bg-muted text-muted-foreground'}`} data-id="element-343" data-mp="2cfvzxfcgm9lt">
                              <PIcon className="size-4" aria-hidden="true" data-id="element-344" data-mp="i2e7pf140okg" />
                            </span>
                            <div data-id="element-345" data-mp="a1gejonshbin">
                              <p className="text-sm font-medium" data-id="element-346" data-mp="a2i1aqe63mg7">{p.title}</p>
                              <p className="text-xs text-muted-foreground" data-id="element-347" data-mp="61y4jnxiakul">{p.description}</p>
                            </div>
                          </li>;
                  })}
                    </ul>
                  </section>;
            })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}